export const overviewContent = String(`# Lightswitch Overview

Lightswitch is a developer platform that makes it easy to ship **professional onboarding, monetization, and analytics** without stitching together half a dozen services. 

We built Lightswitch to solve the recurring pains indie devs and small teams face:
- **Auth & Security gaps** – AI-generated or DIY auth is often insecure or hacky.  
- **Monetization friction** – Setting up paywalls and payments (Stripe, Apple/Google) is confusing and slow.  
- **Onboarding drop-off** – Users churn right after signup due to unclear flows.  
- **Analytics blind spots** – Hard to see where users convert, pay, or drop without heavyweight tools.  
- **Integration headaches** – Payments, auth, and analytics rarely “just work” together.  

Lightswitch bundles these into focused SDKs with clean defaults and copy-paste examples — so you can launch in minutes, not weeks.

---

## Core Modules

### 1. Access & Paywall SDK
Drop-in components for **sign-up, login, entitlement checks, and paywalls**. Built for React + Supabase.  
- Protect routes with a single \`<ProtectedRoute>\`.  
- Auto-show paywalls when a user isn’t entitled.  
- Configure SKUs and entitlements in the admin.  
👉 See [Access & Paywall SDK docs](../access-paywall/overview.md).

### 2. Analytics
Lightweight analytics for **users, revenue, and retention**.  
- Capture key events automatically (signup, paywall viewed, checkout completed).  
- Plug into your Supabase project with no extra setup.  
- View dashboards for revenue, churn, and funnel drop-off.  
👉 See [Analytics docs](../analytics/overview.md).

---

## Why Lightswitch?

Lightswitch is broader and lighter:  
- **Plug-and-play**: One-line install, sensible defaults.  
- **Modular**: Use just Access SDK, just Analytics, or both.  
- **Indie-friendly**: No bloated templates, no heavy dashboards.  
- **Fast integration**: From idea → live onboarding in <30 minutes.  

Our goal: let you focus on building your product, while Lightswitch handles the **plumbing of onboarding, monetization, and analytics**.`);